import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { doc, setDoc, updateDoc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../firebase';
import { FiUser, FiUserPlus, FiCheckCircle, FiAlertCircle, FiSave } from 'react-icons/fi';
import './AdminSetup.css';

const AdminSetup = () => {
  const { user, userProfile, refreshUserData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  
  // Current user profile form
  const [profileForm, setProfileForm] = useState({
    displayName: '',
    email: '',
    globalRole: 'administrator',
    isActive: true
  });

  // Invite admin form
  const [inviteForm, setInviteForm] = useState({
    email: '',
    displayName: ''
  });

  const createUserInvitation = httpsCallable(functions, 'createUserInvitation');

  useEffect(() => {
    if (user && userProfile) {
      setProfileForm({
        displayName: userProfile.displayName || user.displayName || '',
        email: userProfile.email || user.email || '',
        globalRole: userProfile.globalRole || 'administrator',
        isActive: userProfile.isActive !== undefined ? userProfile.isActive : true
      });
    } else if (user) {
      setProfileForm({
        displayName: user.displayName || '',
        email: user.email || '',
        globalRole: 'administrator',
        isActive: true
      });
    }
  }, [user, userProfile]);

  const handleFixCurrentUser = async (e) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError('');
    setMessage('');

    try {
      // Create/update user document with proper structure
      const userDoc = {
        email: profileForm.email,
        displayName: profileForm.displayName,
        globalRole: profileForm.globalRole,
        isActive: profileForm.isActive,
        profile: {
          notifications: {
            email: true,
            push: true
          }
        },
        security: {
          mustChangePassword: false,
          failedLoginAttempts: 0
        },
        metadata: {
          createdAt: new Date(),
          lastLogin: null,
          lastActivity: null,
          updatedAt: new Date(),
          updatedBy: user.uid
        }
      };

      await setDoc(doc(db, 'users', user.uid), userDoc, { merge: true });
      await refreshUserData();
      setMessage('✅ Your admin profile has been created/updated successfully!');
    } catch (err) {
      console.error('Error updating user profile:', err);
      setError('Failed to update profile: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteAdmin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const result = await createUserInvitation({
        email: inviteForm.email,
        role: 'administrator',
        displayName: inviteForm.displayName,
        franchiseId: null
      });

      if (result.data.success) {
        setMessage('✅ Admin invitation sent successfully!');
        setInviteForm({ email: '', displayName: '' });
      }
    } catch (err) {
      console.error('Error sending invitation:', err);
      
      // Handle specific error codes gracefully
      if (err.code === 'functions/invalid-argument' && err.message.includes('Invitation already sent')) {
        // User already has a pending invitation
        setError(`⚠️ An invitation has already been sent to ${inviteForm.email}. Please check if they received it or contact them directly.`);
      } else if (err.code === 'error_409' || err.message.includes('Invitation already sent')) {
        // Alternative 409 error format
        setError(`⚠️ An invitation has already been sent to ${inviteForm.email}. Please check if they received it or resend if needed.`);
      } else if (err.code === 'functions/permission-denied') {
        setError('❌ You do not have permission to send invitations. Please contact an administrator.');
      } else if (err.code === 'functions/invalid-argument') {
        setError('❌ Invalid invitation data. Please check all required fields.');
      } else if (err.message.includes('required')) {
        setError('❌ Missing required fields. Please fill out all required information.');
      } else {
        setError('❌ Failed to send invitation: ' + (err.message || 'Unknown error occurred'));
      }
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="admin-setup">
      <div className="admin-setup-header">
        <h1>🚀 Admin Setup & Management</h1>
        <p>Set up your admin account and invite other administrators</p>
      </div>

      {message && (
        <div className="success-alert">
          <FiCheckCircle />
          <span>{message}</span>
        </div>
      )}

      {error && (
        <div className="error-alert">
          <FiAlertCircle />
          <span>{error}</span>
        </div>
      )}

      <div className="setup-grid">
        {/* Current User Profile Setup */}
        <div className="setup-card">
          <div className="card-header">
            <FiUser className="card-icon" />
            <h3>Fix Your Admin Profile</h3>
          </div>
          <p>Create or update your administrator profile with proper permissions.</p>
          
          <form onSubmit={handleFixCurrentUser} className="profile-form">
            <div className="form-group">
              <label>Display Name</label>
              <input
                type="text"
                value={profileForm.displayName}
                onChange={(e) => setProfileForm(prev => ({
                  ...prev,
                  displayName: e.target.value
                }))}
                placeholder="Your Full Name"
                required
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={profileForm.email}
                onChange={(e) => setProfileForm(prev => ({
                  ...prev,
                  email: e.target.value
                }))}
                placeholder="your@email.com"
                required
                disabled
              />
            </div>

            <div className="form-group">
              <label>Role</label>
              <select
                value={profileForm.globalRole}
                onChange={(e) => setProfileForm(prev => ({
                  ...prev,
                  globalRole: e.target.value
                }))}
              >
                <option value="administrator">Administrator</option>
                <option value="franchisee">Franchisee</option>
                <option value="operator">Operator</option>
              </select>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={profileForm.isActive}
                  onChange={(e) => setProfileForm(prev => ({
                    ...prev,
                    isActive: e.target.checked
                  }))}
                />
                Active Account
              </label>
            </div>

            <button type="submit" className="btn-primary" disabled={loading}>
              <FiSave />
              {loading ? 'Saving...' : 'Fix My Profile'}
            </button>
          </form>
        </div>

        {/* Invite Admin */}
        <div className="setup-card">
          <div className="card-header">
            <FiUserPlus className="card-icon" />
            <h3>Invite Another Admin</h3>
          </div>
          <p>Send an invitation to create another administrator account.</p>
          
          <form onSubmit={handleInviteAdmin} className="invite-form">
            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm(prev => ({
                  ...prev,
                  email: e.target.value
                }))}
                placeholder="admin@company.com"
                required
              />
            </div>

            <div className="form-group">
              <label>Display Name</label>
              <input
                type="text"
                value={inviteForm.displayName}
                onChange={(e) => setInviteForm(prev => ({
                  ...prev,
                  displayName: e.target.value
                }))}
                placeholder="Full Name"
                required
              />
            </div>

            <button type="submit" className="btn-primary" disabled={loading}>
              <FiUserPlus />
              {loading ? 'Sending...' : 'Send Admin Invitation'}
            </button>
          </form>
        </div>


      </div>

      {/* Current Status */}
      <div className="status-section">
        <h3>Current User Status</h3>
        <div className="status-grid">
          <div className="status-item">
            <strong>UID:</strong> {user?.uid || 'Not logged in'}
          </div>
          <div className="status-item">
            <strong>Email:</strong> {user?.email || 'Not available'}
          </div>
          <div className="status-item">
            <strong>Profile Role:</strong> {userProfile?.globalRole || 'Not set'}
          </div>
          <div className="status-item">
            <strong>Active:</strong> {userProfile?.isActive ? 'Yes' : 'No'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSetup; 